import { useCallback, useEffect, useMemo, useState } from "react";
import Toast from "components/ui/tableComponents/Toast";
import {
  getStripeProductsConfig,
  syncStripeProducts,
  updateStripeProductTracking,
} from "services/stripe-product.service";

const formatMoney = (amount, currency = "usd") => {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: (currency || "usd").toUpperCase(),
    }).format(num);
  } catch {
    return `$${num.toFixed(2)}`;
  }
};

const formatInterval = (row) => {
  if (!row.isRecurring) return "One-time";
  const count = row.intervalCount && row.intervalCount > 1 ? `${row.intervalCount} ` : "";
  return `Every ${count}${row.interval || "period"}`;
};

const groupByProduct = (records) => {
  const map = new Map();
  for (const row of records) {
    const key = row.stripeProductId || row.name;
    if (!map.has(key)) {
      map.set(key, {
        stripeProductId: row.stripeProductId,
        name: row.name,
        prices: [],
      });
    }
    map.get(key).prices.push(row);
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
};

export default function StripeProductTrackingPanel({ api }) {
  const [records, setRecords] = useState([]);
  const [trackedByPrice, setTrackedByPrice] = useState({});
  const [savedTracked, setSavedTracked] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const applyRecords = useCallback((list) => {
    const rows = Array.isArray(list) ? list : [];
    setRecords(rows);
    const next = {};
    for (const row of rows) {
      next[row.stripePriceId] = !!row.tracked;
    }
    setTrackedByPrice(next);
    setSavedTracked(next);
  }, []);

  const loadData = useCallback(async () => {
    const data = await getStripeProductsConfig(api, { limit: 500, page: 1 });
    applyRecords(data?.records ?? []);
  }, [api, applyRecords]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        await loadData();
      } catch {
        if (!cancelled) {
          setLoadError("Failed to load Stripe products. Please try again.");
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [loadData]);

  const groups = useMemo(() => groupByProduct(records), [records]);

  const isDirty = useMemo(() => {
    const keys = new Set([
      ...Object.keys(trackedByPrice),
      ...Object.keys(savedTracked),
    ]);
    for (const key of keys) {
      if (!!trackedByPrice[key] !== !!savedTracked[key]) return true;
    }
    return false;
  }, [trackedByPrice, savedTracked]);

  const handleToggle = (stripePriceId, checked) => {
    setTrackedByPrice((prev) => ({ ...prev, [stripePriceId]: checked }));
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setLoadError(null);
    try {
      await syncStripeProducts(api);
      await loadData();
      setToast({
        show: true,
        message: "Stripe products synced successfully",
        type: "success",
      });
    } catch {
      setToast({
        show: true,
        message: "Failed to sync Stripe products",
        type: "error",
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSave = async () => {
    const items = Object.keys(trackedByPrice)
      .filter((priceId) => !!trackedByPrice[priceId] !== !!savedTracked[priceId])
      .map((stripePriceId) => ({
        stripePriceId,
        tracked: !!trackedByPrice[stripePriceId],
      }));

    if (items.length === 0) return;

    setIsSaving(true);
    try {
      await updateStripeProductTracking(api, items);
      await loadData();
      setToast({
        show: true,
        message: "Product tracking saved successfully",
        type: "success",
      });
    } catch {
      setToast({
        show: true,
        message: "Failed to save product tracking",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setTrackedByPrice({ ...savedTracked });
  };

  const busy = isLoading || isSyncing || isSaving;

  return (
    <div className="mt-8">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              Stripe Product Tracking
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Select which synced Stripe prices participate in BV1 sales and
              dashboard analytics. New Stripe products default to untracked.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleSync}
              disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isSyncing ? "Syncing..." : "Sync Stripe Products"}
            </button>
          </div>
        </div>

        <div className="p-6">
          {loadError && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {loadError}
            </div>
          )}

          {isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-gray-100 animate-pulse rounded-lg" />
              ))}
            </div>
          ) : groups.length === 0 ? (
            <p className="text-sm text-gray-500">
              No Stripe products found. Click &quot;Sync Stripe Products&quot; to
              pull the catalog from Stripe.
            </p>
          ) : (
            <div className="space-y-6 max-h-[32rem] overflow-y-auto pr-1">
              {groups.map((group) => (
                <div key={group.stripeProductId} className="border border-gray-200 rounded-lg">
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <div className="font-medium text-gray-900">{group.name}</div>
                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                      {group.stripeProductId}
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {group.prices.map((row) => (
                      <label
                        key={row.stripePriceId}
                        className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded"
                          checked={!!trackedByPrice[row.stripePriceId]}
                          disabled={busy}
                          onChange={(e) =>
                            handleToggle(row.stripePriceId, e.target.checked)
                          }
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
                            <span className="font-medium">
                              {formatMoney(row.amount, row.currency)}
                            </span>
                            <span className="text-gray-500">{formatInterval(row)}</span>
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs ${
                                row.isActive
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {row.isActive ? "Active" : "Inactive"}
                            </span>
                            {row.internalKey && row.internalKey !== "unmapped" && (
                              <span className="text-xs text-gray-400 font-mono">
                                {row.internalKey}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500 font-mono mt-1 truncate">
                            {row.stripePriceId}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={handleCancel}
              disabled={busy || !isDirty}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !isDirty}
              className="px-6 py-2 text-sm bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition disabled:opacity-50"
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
}
